import {pdf} from "@react-pdf/renderer";
import {useCallback} from "react";
import StatementPdf, {type ReceiptData} from "@/components/Pdf/StatementPdf.tsx";
import {useUser} from "@/components/Providers/JWTProvider.tsx";

const useGetTaxStatementPdf = (): ((year: string) => Promise<Blob | void>) => {
    const {user} = useUser();

    return useCallback(
        async (year: string): Promise<Blob | void> => {
            if (!user) {
                console.error("User not authenticated");
                return;
            }

            if (!("apiFetch" in user)) {
                console.error("apiFetch not available");
                return;
            }

            // Fetch payment data from API (studentId comes from authenticated user context)
            const url = new URL(
                `/v1/tax-statement/data/${year}`,
                import.meta.env.VITE_APP_API_ENDPOINT,
            );

            const response = await user.apiFetch(url.toString(), {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            if (!response) {
                throw new Error("Failed to fetch tax statement data");
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch tax statement data: ${response.statusText}`);
            }

            const data: ReceiptData = await response.json();

            // Generate PDF client-side using @react-pdf/renderer
            return await pdf(<StatementPdf data={data} />).toBlob();
        },
        [user],
    );
};

export default useGetTaxStatementPdf;
